/**
 * 20240326
 * 
 * 阿里云日志服务
 * 记录用户行为，仅用来排查问题
 */

/**
 * 公共参数
 * url: 页面地址
 * ua: userAgent
 */
import { HelperCore, SlsTracker } from "@src/common/dependencies";
import client360Store from "@public/client/client360";

class SLSTracker {
  tracker = null;
  baseData = {};
  errorReported = [];
  qapmUidCount = 0;

  constructor () {
    this.tracker = new SlsTracker({
      host: 'cn-beijing.log.aliyuncs.com',
      project: 'nami',
      logstore: 'namibot',
      time: 10,
      count: 10,
      topic: 'topic',
      source: 'source',
      tags: {
        tags: 'tags',
      },
    });
    this.initBaseData();
    this.checkQapmUid();

    window.addEventListener("error", (event) => {
      const tagName = event.target.tagName;
      if(/^img|video$/i.test(tagName)){
        const src = event.target.src;
        if(!this.isInvalidError(src)){
          this.record({
            action : "error",
            arrt : "resource",
            ext : tagName,
            ext1 : src,
          });
        }
        return;
      }

      if(/^script$/i.test(tagName)){
        const src = event.target.src;

        /**
         * 白名单:
         *  1. 用户中心登录 SDK
         */ 
        if(src && /local/.test(src)){
          return;
        }

        if(!this.isInvalidError(src)){
          this.record({
            action : "error",
            arrt : "script",
            ext : tagName,
            ext1 : src,
          });
        }
        return;
      }

      if(!this.isInvalidError(event.message)){
        this.record({
          action : "error",
          arrt : event.type,
          ext : event.message,
        });
      }
    }, true);
    window.addEventListener("unhandledrejection", (event) => {
      if(!this.isInvalidError(event.reason?.stack || "")){
        this.record({
          action : "error",
          arrt : event.type,
          ext : event.reason?.stack || "",
        });
      }
    });
    window.onerror = (message, source, lineno, colno, error) => {
      if(!this.isInvalidError(error?.stack || message)){
        this.record({
          action : "error",
          arrt : "onerror",
          ext : [lineno, colno].join(":"),
          ext1 : error?.stack || message,
          ext2 : source,
        });
      }
    }
    window.SLS_HAS_LOADED = true;
    if(window.SLS_ERROR_LIST){
      window.SLS_ERROR_LIST.forEach(item => {
        const { tagName, src } = item;

        if(!this.isInvalidError(src)){
          this.record({
            action : "error",
            arrt : /^script$/i.test(tagName) ? "script" : "resource",
            ext : tagName,
            ext1 : src,
          });
        }
      });
      // debug
      // window.SLS_ERROR_LIST = [];
    }
  }

  /**
   * 无效报错，不上报
   * @param {String} message 
   * @param {Error} error 
   */
  isInvalidError(message, error) {
    // 无效信息
    if(!message || message == "undefined"){
      return true;
    }
    // 插件报错
    if(message.includes("chrome-extension://")){
      return true;
    }
    // 插件报错
    // `Uncaught SyntaxError
    // : Unexpected token '<'`
    if(/^Uncaught\sSyntaxError([\s\:]*)Unexpected\stoken\s\'\<\'/.test(message)){
      return true;
    }
    // 是否已上报过
    if(this.errorReported.includes(message)){
      return true;
    }

    this.errorReported.push(message);
    return false;
  }

  initBaseData () {
    this.setBaseData({
      key : "assistant_fe",
      // 360 浏览器 mid || 用户本地 id  
      souid : client360Store.sid,
      // 360 浏览器 mid
      mid : client360Store.mid,
      // 用户本地 id 
      feid : client360Store.feid,
      // ua
      ua : HelperCore.initUa(),
      // platform
      platform : HelperCore.isPhone() ? "h5" : "web",
      // 版本号
      version : HelperCore.revision?.time || "",
      // 页面开始加载时间
      page_begin : window._page_load_begin || "",
      // 环境
      branch : HelperCore.revision?.branch || "",
      mode : import.meta.env.MODE,
    });
  }

  /**
   * qapm uid
   */
  checkQapmUid () {
    if(!window.__QAPM__){
      return;
    }
    if(this.baseData.finersid){
      return;
    }

    try{
      this.setBaseData({
        finersid : Qapm.instance.uid,
      });
    } catch(e){}
    if(!this.baseData.finersid && ++this.qapmUidCount < 10){
      setTimeout(() => {
        this.checkQapmUid();
      }, 300);
    }
  }

  /**
   * 立即上报
   */
  log (params) {
    this.tracker.sendImmediate(this.__getFixedParams(params));
  }

  /**
   * 根据配置的 time 和 count 参数延时上报
   * 如果后续调用了 log，会携带 record 一起上报
   */
  record (params){
    this.tracker.send(this.__getFixedParams(params));
  }

  setBaseData (params) {
    this.baseData = {
      ...this.baseData,
      ...params,
    }
  }

  __getFixedParams (params) {
    return {
      url : location.href,
      ...this.baseData,
      ...params,
    }
  }
}

const slsLogger = new SLSTracker();

if(!HelperCore.IS_PROD){
  window._slsLogger = slsLogger;
}

export default slsLogger;

export const createSlsLogger = (name, ignoreLocal = true) => {
  const baseData = {
    log_type : name,
  }

  return {
    log : params => {
      if(HelperCore.IS_LOCAL && ignoreLocal){
        console.log(`SLS local:`, params);
        return;
      }

      slsLogger.log({
        ...baseData,
        ...params,
      });
    },
    setBaseData : params => {
      Object.assign(baseData, params);
    },
  }
}